import type { RangeValue } from "@react-types/shared";
import { useNavigate } from "@tanstack/react-router";
import { ChevronDown, File05, SearchMd, Star06 } from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { DateValue } from "react-aria-components";
import { usePagination } from "@/api/pagination";
import { useDeleteSmartBrief, useSmartBriefs, useSmartBriefViewCounts } from "@/api/smart-briefs";
import { DateRangePicker } from "@/components/application/date-picker/date-range-picker";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Input } from "@/components/base/input/input";
import { PageLayout } from "@/components/page-layout";
import { useSort } from "@/hooks/use-sort";
import { useMerchant } from "@/providers/MerchantProvider";
import { error, success } from "@/providers/ToastProvider";
import { ProductSelectionModal } from "./components/ProductSelectionModal";
import { SmartBriefEditModal } from "./components/SmartBriefEditModal";
import type { TheaterProps } from "./SmartBriefsLandingPage";
import { SmartBriefsTable } from "./smart-briefs-table";

interface SmartBriefsListProps {
  onNavigateToLanding?: () => void;
  /** Theater props for router-level theater */
  theaterProps?: TheaterProps;
}

type SmartBrief = NonNullable<ReturnType<typeof useSmartBriefs>["data"]>[number];

const mergeViewCounts = (briefs: SmartBrief[], viewCounts: Record<string, number>) =>
  briefs.map((brief) => ({ ...brief, viewCount: viewCounts[brief.id] ?? 0 }));

export type SmartBriefRow = ReturnType<typeof mergeViewCounts>[number];

export const SmartBriefsListPage = ({ onNavigateToLanding, theaterProps }: SmartBriefsListProps) => {
  const navigate = useNavigate();
  const merchant = useMerchant();
  const [showProductModal, setShowProductModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<string>("all");
  const [dateRange, setDateRange] = useState<RangeValue<DateValue> | null>();
  // State for edit modal - opens when clicking edit on a brief
  const [editingBriefId, setEditingBriefId] = useState<string | null>(null);
  const [editingProductName, setEditingProductName] = useState<string | undefined>(undefined);

  const { data: briefs = [], isLoading } = useSmartBriefs();
  const { data: viewCounts = {} } = useSmartBriefViewCounts();

  const briefRows = useMemo(() => mergeViewCounts(briefs, viewCounts), [briefs, viewCounts]);

  // Auto-open product modal if there's a preselected product from URL
  useEffect(() => {
    if (theaterProps?.preselectedProductId || theaterProps?.preselectedProductName) {
      setShowProductModal(true);
    }
  }, [theaterProps?.preselectedProductId, theaterProps?.preselectedProductName]);

  // Extract unique product names from briefs for filtering
  const products = useMemo(() => {
    const productNames = briefs.map((brief) => brief.productName).filter(Boolean);
    return [...new Set(productNames)];
  }, [briefs]);

  const deleteBrief = useDeleteSmartBrief();

  // Pagination and sorting
  const pagination = usePagination();
  const { sort, setFromDescriptor, descriptor } = useSort<
    "title" | "createdAt" | "updatedAt" | "productName" | "views"
  >("createdAt");

  // Helper function to check if a date falls within the selected date range
  const isWithinDateRange = useCallback(
    (dateString: string) => {
      if (!dateRange) return true;

      const briefDate = new Date(dateString);
      const reportDate = new Date(briefDate.getFullYear(), briefDate.getMonth(), briefDate.getDate());
      const fromDate = new Date(
        dateRange.start.toDate("UTC").getFullYear(),
        dateRange.start.toDate("UTC").getMonth(),
        dateRange.start.toDate("UTC").getDate(),
      );
      const toDate = new Date(
        dateRange.end.toDate("UTC").getFullYear(),
        dateRange.end.toDate("UTC").getMonth(),
        dateRange.end.toDate("UTC").getDate(),
      );

      return reportDate >= fromDate && reportDate <= toDate;
    },
    [dateRange],
  );

  // Filter and sort briefs
  const filteredAndSortedBriefs = useMemo(() => {
    // First filter
    const filtered = briefRows.filter((brief) => {
      // Text search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        if (!brief.title.toLowerCase().includes(searchLower)) {
          return false;
        }
      }

      // Product filter
      if (selectedProduct !== "all" && brief.productName !== selectedProduct) {
        return false;
      }

      // Date filter
      const matchesDateRange = isWithinDateRange(brief.createdAt);
      if (!matchesDateRange) {
        return false;
      }

      return true;
    });

    // Then sort
    return [...filtered].sort((a, b) => {
      if (!sort.column) return 0;

      let aValue: string | Date | number;
      let bValue: string | Date | number;

      switch (sort.column) {
        case "title":
          aValue = a.title;
          bValue = b.title;
          break;
        case "createdAt":
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case "updatedAt":
          aValue = new Date(a.updatedAt);
          bValue = new Date(b.updatedAt);
          break;
        case "productName":
          aValue = a.productName;
          bValue = b.productName;
          break;
        case "views":
          aValue = a.viewCount;
          bValue = b.viewCount;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sort.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sort.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [briefRows, searchTerm, selectedProduct, sort.column, sort.direction, isWithinDateRange]);

  // Calculate pagination
  const paginatedBriefs = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.pagination.limit;
    const endIndex = startIndex + pagination.pagination.limit;
    return filteredAndSortedBriefs.slice(startIndex, endIndex);
  }, [filteredAndSortedBriefs, pagination.page, pagination.pagination.limit]);

  const handleDelete = async (briefId: string) => {
    try {
      await deleteBrief.mutateAsync(briefId);
      success("Brief deleted successfully!");
    } catch (_error) {
      error("Failed to delete brief");
    }
  };

  // Navigation handlers
  const handleViewBrief = (briefId: string) => {
    navigate({ to: "/merchant/$merchantId/smart-briefs/brief/$briefId", params: { merchantId: merchant, briefId } });
  };

  const handleEditBrief = (briefId: string) => {
    // Open edit modal directly
    setEditingBriefId(briefId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading briefs...</span>
      </div>
    );
  }

  return (
    <PageLayout
      title="Smart Product Briefs"
      icon={File05}
      subtitle="Your list of all smart briefs based on intelligent insights to give to creators"
      callToAction={
        <Button
          iconLeading={<Star06 className="w-4 h-4 mr-2" />}
          onClick={() => setShowProductModal(true)}
          className="flex items-center justify-between primary"
        >
          Generate new brief
        </Button>
      }
    >
      <div className="flex-1 flex flex-col min-h-0">
        {/* Navigation - Single Row with Filters, Count, and Search */}
        <div className="flex flex-shrink-0 flex-row items-center justify-between pb-6 rounded-xl gap-4">
          {/* Left side: Product Filter and Brief Count */}
          <div className="flex items-center flex-row gap-4">
            {/* Product Filter */}
            <Dropdown.Root>
              <Button
                size="sm"
                color="secondary"
                className="text-sm font-semibold"
                iconTrailing={<ChevronDown className="w-4 h-4" />}
              >
                {selectedProduct === "all" ? "All Products" : selectedProduct}
              </Button>
              <Dropdown.Popover placement="bottom left">
                <Dropdown.Menu
                  selectedKeys={[selectedProduct]}
                  onSelectionChange={(keys) => {
                    const selected = Array.from(keys)[0] as string;
                    setSelectedProduct(selected);
                  }}
                >
                  <Dropdown.Item label="All Products" id="all" />
                  {products.map((product) => (
                    <Dropdown.Item key={product} label={product} id={product} />
                  ))}
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown.Root>

            {/* Brief Count */}
            <span className="text-sm font-semibold text-gray-500">
              {filteredAndSortedBriefs.length} Brief{filteredAndSortedBriefs.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Right side: Search and Date Filter */}
          <div className="flex items-center flex-row gap-2">
            <Input
              size="sm"
              inputClassName="text-sm font-semibold"
              className="w-96"
              value={searchTerm}
              onClear={() => setSearchTerm("")}
              onChange={setSearchTerm}
              placeholder="Search"
              icon={SearchMd}
            />

            <DateRangePicker
              textClassName={"text-sm font-semibold"}
              aria-label="Date range picker"
              shouldCloseOnSelect={false}
              selected={dateRange ? { start: dateRange.start, end: dateRange.end } : null}
              value={dateRange}
              onChange={(newRange) => {
                setDateRange(newRange);
                pagination.setPage(1); // Reset pagination when date range changes
              }}
              onReset={() => setDateRange(null)}
              resetText="Clear"
              isActive={!!dateRange}
            />
          </div>
        </div>

        {/* Table */}
        <div className="flex flex-col w-full flex-1 min-h-0">
          <SmartBriefsTable
            rows={paginatedBriefs}
            onClickRow={(row) => handleViewBrief(row.id)}
            sorting={{ descriptor, onSortChange: setFromDescriptor }}
            pagination={{
              page: pagination.page,
              totalPages: Math.ceil((filteredAndSortedBriefs.length ?? 0) / pagination.pagination.limit),
              onPageChange: pagination.setPage,
            }}
            onNavigateToLanding={onNavigateToLanding}
            onDeleteBrief={handleDelete}
            onEditBrief={handleEditBrief}
          />
        </div>
      </div>

      {/* Product Selection Modal */}
      <ProductSelectionModal
        isOpen={showProductModal}
        onClose={() => {
          setShowProductModal(false);
          // Clear URL preselection when modal is closed
          theaterProps?.onPreselectionProcessed?.();
        }}
        onBriefCreated={(briefId, productName) => {
          // Open edit modal directly on list page instead of navigating
          setEditingBriefId(briefId);
          setEditingProductName(productName);
          // Clear URL preselection after brief is created
          theaterProps?.onPreselectionProcessed?.();
        }}
        theaterProps={theaterProps}
        preselectedProductId={theaterProps?.preselectedProductId}
        preselectedProductName={theaterProps?.preselectedProductName}
      />

      {/* Edit Modal - opens on list page after brief creation or when clicking edit */}
      {editingBriefId && (
        <SmartBriefEditModal
          isOpen={!!editingBriefId}
          onClose={() => {
            setEditingBriefId(null);
            setEditingProductName(undefined);
          }}
          briefId={editingBriefId}
          productName={editingProductName}
        />
      )}
    </PageLayout>
  );
};
